import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Mail, Menu, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Button from '../ui/Button.tsx';
import LanguageSelector from '../ui/LanguageSelector.tsx';
import { useAuth } from '../../contexts/AuthContext.tsx';

interface HeaderProps {
  currentPage: string;
  onNavigate: (page: string) => void;
}

const Header: React.FC<HeaderProps> = ({ currentPage, onNavigate }) => {
  const { t } = useTranslation();
  const { isAuthenticated, user } = useAuth();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { name: t('header.navigation.home'), key: 'home' },
    { name: t('header.navigation.deploy'), key: 'deploy' },
    { name: t('header.navigation.pricing'), key: 'plans' },
    { name: t('header.navigation.roadmap'), key: 'roadmap' },
    { name: t('header.navigation.contact'), key: 'contact' },
  ];

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center justify-between">
          <div className='flex items-center space-x-16'>
            <div className="flex items-center cursor-pointer group" onClick={() => onNavigate('home')}>
              <div className="flex items-center justify-center mr-2">
                <img
                  src="/kexa-no-background-color.png"
                  alt="Kexa Logo"
                  className="w-8 h-8 rounded-full"
                />
              </div>
            </div>
            
            {/* Navigation */}
            <nav className="hidden lg:flex items-center space-x-8">
              {navItems.map((item) => (
                <button
                  key={item.key}
                  onClick={() => onNavigate(item.key)}
                  className={`relative text-sm font-medium transition-colors hover:text-primary ${
                    currentPage === item.key
                      ? 'text-primary'
                      : 'text-gray-600'
                  }`}
                >
                  {item.name}
                  {currentPage === item.key && (
                    <motion.div
                      layoutId="activeTab"
                      className="absolute -bottom-1 left-0 right-0 h-0.5 bg-primary"
                    />
                  )}
                </button>
              ))}
            </nav>
          </div>
          <div className="hidden lg:flex items-center space-x-8">
            <Button
              onClick={() => onNavigate(isAuthenticated ? 'account' : 'auth')}
              variant="secondary"
              size="sm"
            >
              {isAuthenticated && user ? user.firstname : t('header.buttons.account')}
            </Button>
            <Button
              href="https://github.com/4urcloud/kexa"
              target="_blank"
              rel="noopener noreferrer"
              variant="outline"
              size="sm"
              className="border-gray-300 text-gray-700 hover:border-primary hover:text-primary"
              leftIcon={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-5 h-5 text-primary"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M15 22v-4a4.8 4.8 0 0 0-1-3.5c3 0 6-2 6-5.5.08-1.25-.27-2.48-1-3.5.28-1.15.28-2.35 0-3.5 0 0-1 0-3 1.5-2.64-.5-5.36-.5-8 0C6 2 5 2 5 2c-.3 1.15-.3 2.35 0 3.5A5.403 5.403 0 0 0 4 9c0 3.5 3 5.5 6 5.5-.39.49-.68 1.05-.85 1.65-.17.6-.22 1.23-.15 1.85v4" />
                  <path d="M9 18c-4.51 2-5-2-7-2" />
                </svg>
              }
            >
              {t('header.buttons.ossCore')}
            </Button>
            <Button
              onClick={() => onNavigate('contact')}
              variant="primary"
              size="sm"
              leftIcon={<Mail size={16} />}
            >
              {t('header.buttons.contact')}
            </Button>
            <LanguageSelector />
          </div>
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden text-gray-600 hover:text-primary transition-colors"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white/95 backdrop-blur-md shadow-lg border-t border-gray-200"
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-2">
              {navItems.map((item) => (
                <button
                  key={item.key}
                  onClick={() => {
                    onNavigate(item.key);
                    setIsMobileMenuOpen(false);
                  }}
                  className={`block w-full text-left py-4 px-4 rounded-lg text-base font-medium transition-all duration-200 hover:bg-gray-50 active:scale-95 ${
                    currentPage === item.key 
                      ? 'text-primary bg-primary/5' 
                      : 'text-gray-700 hover:text-primary'
                  }`}
                >
                  {item.name}
                </button>
              ))}
              
              <div className="pt-6 mt-6 border-t border-gray-200">
                <div className="flex flex-col space-y-3">
                  <Button
                    onClick={() => {
                      onNavigate(isAuthenticated ? 'account' : 'auth');
                      setIsMobileMenuOpen(false);
                    }}
                    variant="secondary"
                    size="sm"
                    className="w-full"
                  >
                    {isAuthenticated && user ? user.firstname : t('header.buttons.account')}
                  </Button>
                  <div className="flex items-center justify-center space-x-6 pt-4">
                    <a
                      href="https://github.com/4urcloud/kexa"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-3 text-gray-600 hover:text-primary transition-colors hover:bg-gray-50 rounded-lg"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="w-6 h-6"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M15 22v-4a4.8 4.8 0 0 0-1-3.5c3 0 6-2 6-5.5.08-1.25-.27-2.48-1-3.5.28-1.15.28-2.35 0-3.5 0 0-1 0-3 1.5-2.64-.5-5.36-.5-8 0C6 2 5 2 5 2c-.3 1.15-.3 2.35 0 3.5A5.403 5.403 0 0 0 4 9c0 3.5 3 5.5 6 5.5-.39.49-.68 1.05-.85 1.65-.17.6-.22 1.23-.15 1.85v4" />
                        <path d="M9 18c-4.51 2-5-2-7-2" />
                      </svg>
                    </a>
                    <button
                      onClick={() => {
                        onNavigate('contact');
                        setIsMobileMenuOpen(false);
                      }}
                      className="p-3 text-gray-600 hover:text-primary transition-colors hover:bg-gray-50 rounded-lg"
                    >
                      <Mail size={24} />
                    </button>
                    <LanguageSelector />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;